import AlertArea from "../ui/AlertArea.js";
import AssistantArea from "../ui/AssistantArea";
import InputTextArea from "../ui/InputTextArea.js";

import GisNode from "./GisNode.js";

import Error from "../utils/Error.js";
import global from "../global.js";
import RendererStaticMap from "./RendererStaticMap.js";
import Media from "../utils/Media.js";

const log = global.log.getLogger("f_mtac.scene.map_gis");
log.setLevel(log.levels.WARN);

/**
 * @brief MapGis is reponsible for
 *  . displaying the background map which is a GIS (googlemap, OSM, ...)
 * @param {*} view
 * @param {*} svg
 * @param {Map} map instance of scene/Grid
 */
const MapGis = function (view, svg, objects, map) {
    let gis_node = new GisNode(view); // a node for a map (google or Open street maps)
    let fallback_media_id = null;

    log.debug("MapGis.ctor", {view, svg, map});

    Object.defineProperties(this, {
        gis_node: {
            set: () => {},
            get: () => gis_node
        },
        fallback_media_id: {
            // used to display static image in case of network loss or gis unauthorized
            set: () => {},
            get: () => fallback_media_id
        },
        is_gis_authorized: {
            // used to know if an org is authorized to use GIS or only fallback map
            set: () => {},
            get: () => {
                return global.view.is_gis_authorized;
            }
        }
    });

    /**
     * @brief Handle the "creating" event received after gis map storage creation
     * @param local_id, the local_id of the object created (see indexedDB)
     * @param new_map_gis, the object_data attributes that can be found in indexedDB storage
     */
    this.on_creating_gis = async (local_id, new_map_gis) => {
        log.debug("MapGis.on_creating_gis", {local_id});

        map.grid.is_extended = true;

        if (global.server.network.state === "online" && this.is_gis_authorized) {
            // this is for GIS
            if (await update_gis(new_map_gis.dataGis)) {
                map.grid.configuration.load(new_map_gis.dataGrid);
                if (new_map_gis.dataCommonGrid !== undefined) {
                    // ADAPT COMMON GRID SETTINGS TO THE CURRENT DESKTOP
                    await map.grid.init_gis_from_common_grid(new_map_gis.dataGrid, new_map_gis.dataCommonGrid);
                }
                map.grid.init_axis();
                map.grid.draw();

                global.view.ol_driver.launch_transform();

                global.tools.tools["geocoding"].$button.css("display", null);
                global.tools.tools["recenter"].$button.css("display", null);
                global.tools.tools["map_type"].$button.css("display", null);
                global.tools.tools["map_type_assistant"].$button.hide();
            }

            // set media cache (in case of application reloading or sharing)
            if (new_map_gis.dataMedia != "media-000102030405060708090a0b0c0d0e0f") {
                fallback_media_id = new_map_gis.dataMedia;

                let state = global.view.ol_driver.get_initial_frame();
                // if tiles used for generate static map are undefined, create them : really needed?
                if (state.tiles === undefined) {
                    global.view.ol_driver.save_initial_tiles();
                }
                state = global.view.ol_driver.get_initial_frame();
                state.zoom = new_map_gis.dataGis.zoom;
                global.view.save_map_gis_context(fallback_media_id, state);
            }
        } else {
            // in change_context case and offline mode get the selected_context data to recover it when back to online network
            map.context.data.type = "gis";
            if (this.is_gis_authorized) {
                // GIS authorized but offline
                let gis = JSON.parse(JSON.stringify(new_map_gis.dataGis));
                if (global.storage.selected_context.data.gis) {
                    // context has already been initialised
                    global.view.ol_driver.current_viewState.center = global.storage.selected_context.data.gis.center;
                    global.view.ol_driver.current_viewState.resolution = global.storage.selected_context.data.gis.resolution;
                    global.view.ol_driver.ol_customer_driver.mapBasicLayerType = global.storage.selected_context.data.gis.map_basic_layer_type;
                    fallback_media_id = global.storage.selected_context.data.gis.fallback_media_id;
                } else {
                    // context not yet initialised (case if offline state after sharing and sitac not selected yet (AGPOC-6761))
                    global.view.ol_driver.current_viewState.center = gis.viewState.center;
                    global.view.ol_driver.current_viewState.resolution = gis.viewState.resolution;
                    fallback_media_id = new_map_gis.dataMedia;
                }
                global.view.ol_driver.initial_viewState.center = gis.viewState.center;
                global.view.ol_driver.initial_viewState.resolution = gis.viewState.resolution;
                global.view.ol_driver.initial_size = gis.size;
                let state = global.view.ol_driver.get_initial_frame();
                global.view.save_gis_context(state);
                global.view.save_map_gis_context(fallback_media_id, state);
            }
        }
    };

    /**
     * Callback when map gis object data is modified
     */
    this.on_updating_gis = async (new_map_gis) => {
        log.debug(`MapGis -> updating" new_map_gis = ${JSON.stringify(new_map_gis)}`);

        // for the moment the only possible modification on GIS is the mapType
        global.view.ol_driver.ol_customer_driver.update_map_basiclayer_type(new_map_gis.dataGis.mapType);
        global.view.save_map_type_gis_context(new_map_gis.dataGis.mapType);
    };

    /**
     * Callback when map gis object is deleted
     */
    this.on_deleting_gis = async (local_id, old_map_gis) => {
        log.debug("MapGis.on_deleting_gis", {local_id, old_map_gis});
        unset_gis();
    };

    /**
     * center: set the map at the center of the window
     * @returns
     */
    this.center = (object) => {
        let gis = JSON.parse(JSON.stringify(object.attributes.dataGis));
        log.debug("MapGis.center", gis);
        // init the center and the resolution so that all the initial  
        // viewstate holds in the full screen
        global.view.ol_driver.init_viewstate_full_screen(gis);
    };

    /**
     * store gis map cache for each map layer type in database
     * @param {*} media_id
     * @param {*} map_type
     * @returns
     */
    const set_map_fallback_media = (media_id, map_type) => {
        let object = map.get_map_object();
        if (object === null) {
            return;
        }
        // fallback media is managed differently depending of ol driver type (Google maps, osm,...)
        return global.view.ol_driver.set_map_fallback_media(media_id, map_type, object);
    };

    // function called to update gis layer in local database and for sharing
    this.update_gis_layer_type = (type) => {
        return map.edit_map_object((object) => {
            object.attributes.dataGis.mapType = type;
        });
    };

    /**
     * After click on the add-gis button:
     * load Google Maps and wait for area selection
     */
    $("body").on("click", "#map_area button.add-gis", async () => {
        log.debug("MapGis click on add-gis");

        // Activate OpenLayers
        const activated = await gis_node.activate();
        log.debug({activated});
        if (!activated) {
            await AlertArea.interact({
                type: "danger",
                tip1: global.internationalization.text("media.Googlemap-load-failure")
            });
            return;
        }
        map.set_map_type("gis");

        // Open setup assistant
        show_gis_init_assistant_tools();
        let confirmation = await AssistantArea.interact();
        if (!confirmation) {
            // Setup cancelled by the user
            cancel_gis();
            return;
        }

        // AGPOC-9892 : OSM static map
        // if zoom not integer => round zoom to integer and wait for render framestate calculation
        // if not => go on
        let zoom = global.view.ol_driver.ol_map.getView().getZoom();
        let rounded_zoom = Math.round(global.view.ol_driver.ol_map.getView().getZoom());
        if (zoom !== rounded_zoom) {
            log.debug("OSM zoom not integer => round and wait for rendering");
            global.view.ol_driver.sitac_creation_ongoing = true;
            global.view.ol_driver.ol_map.getView().setZoom(rounded_zoom);
            // Now let OpenLayersDriver.layer.render() function continue the frame creation when appropriate.
        } else {
            this.continue_initial_frame_creation();
        }
    });

    // function called to initiate sitac frame and context
    this.continue_initial_frame_creation = async () => {
        log.debug("continue_initial_frame_creation IN");
        // get the initial frame before interaction with InputTextArea because the keyboard reduces the frame
        global.view.ol_driver.save_initial_frame();
        global.view.ol_driver.save_initial_tiles();
        let state = global.view.ol_driver.get_initial_frame();

        // save the current context state
        global.view.save_gis_context(state);

        // ask for the SITAC name, propose the last geocoding address
        let address = global.view.ol_driver.get_geocoding_address();
        global.view.save_geocoding_gis_context(address);
        let name = await InputTextArea.interact({
            tip: global.internationalization.text("sitac.Tactical-situation-name"),
            current_text: address.name
        });
        if (name === null) {
            // cancelling name operation cancels the entire operation
            cancel_gis();
            return;
        }

        // save the reference view for GIS
        this.set_map_initial_gis(state);

        await create_gis_object(name, state);
        create_gis_fallback_image(state);

        // create geonodes to allow geolocation
        await global.view.ol_driver.create_gis_geonodes(state);

        // Show menus and buttons for GIS SITAC
        global.tools.tools["recenter"].$button.css("display", null);
        $("#command_area_left").show();
        $("#command_map_right .btn-switcher-right").show();
        $("#desktop_menu").css("display", null);
        $("#desktop_tools").css("display", null);
        $("#command_map_right").css("display", null);
    };

    /**
     * set_map_initial_gis: save GIS initial frame settings
     * @param {Object} gis : initial frame settings
     * @returns
     */
    this.set_map_initial_gis = (gis) => {
        log.debug("MapGis.set_map_initial_gis", {gis});
        let object = map.get_map_object();
        if (object === null) {
            return;
        }
        object.attributes.dataGis = gis; //JSON.parse(JSON.stringify(gis));

        return objects.tx_update("media", object);
    };

    /**
     * create_gis_object: create GIS data, called when the user creates the GIS dekstop
     * @param {string} name,
     * @param {object} gisState, contains map parameters: center, resolution, window
     * @returns
     */
    const create_gis_object = async (name, gisState) => {
        log.debug("MapGis.create_gis_object", {name, gisState});
        if (name === undefined) {
            throw new Error.build.invalid({
                message: "background image name is required, can't continue sorry"
            });
        }

        // dataMedia: "media-000102030405060708090a0b0c0d0e0f" is not useful for GIS,
        // but a control is done at least in the backend,
        // so the value is set to limit modifications !!!!
        // it could be used for Google static images

        // get default configuration
        let dataGrid = map.grid.get_default_configuration();

        log.debug(`MapGis.create_gis_object:  bckg size ${map.width} ${map.height}`);

        dataGrid.grid.size.width = map.width; // this works
        dataGrid.grid.size.height = map.height; // this works
        dataGrid.grid.common_size = dataGrid.grid.size;

        // string with all common grid data
        let str_common_grid = "";
        // find OpenLayers common grid attributes
        let common_data = await global.storage.selected_space.get_grid_common_data();
        if (common_data !== undefined) {
            // set OpenLayers common grid attributes to the current grid
            str_common_grid = common_data;
            log.debug("MapGis.create_gis_object: str_common_grid = ", str_common_grid);

            let common_grid = JSON.parse(str_common_grid);
            if (common_grid.reference_position != undefined) {
                dataGrid.frame.reference.position = common_grid.reference_position;
                dataGrid.frame.color = common_grid.reference_colors;
                log.debug(`MapGis.create_gis_object: use common frame.reference.position = ${common_grid.reference_position}`);
            }
        } else {
            // INITIATE COMMON GRID DATA (first desktop of the room)
            str_common_grid = map.grid.init_common_grid_from_gis(dataGrid);
        }

        //log.debug(`MapGis.create_gis_object dataGrid = ${JSON.stringify(dataGrid)}`);

        dataGrid.grid.color = "#305dd0"; // blue  = default grid color for GIS
        return objects.tx_create("media", {
            dataMedia: "media-000102030405060708090a0b0c0d0e0f",
            dataType: "gis",
            dataSubtype: "background_image",
            dataName: name,
            dataBrightness: 1,
            dataGis: gisState,
            dataGrid: dataGrid,
            dataCommonGrid: str_common_grid,
            dataProjection: null
        });
    };

    /**
     * create_gis_fallback_image: create GIS static map
     * @param {object} gisState, contains map parameters: center, resolution, window
     * @returns
     */
    const create_gis_fallback_image = async (gisState) => {
        log.debug("MapGis.create_gis_fallback_image ", gisState);
        let staticMap = new RendererStaticMap(gisState, map);
        let blob = await staticMap.createStaticMap();
        var media = await Media.binary_to_image(blob, true);
        media = await global.storage.selected_space.media_create(media);
        fallback_media_id = media.local_id;
        global.view.save_map_gis_context(fallback_media_id);
        // mise à jour de l'objet media associé
        await map.set_map_object({
            media_id: media.local_id
        });
        await set_map_fallback_media(media.local_id, gisState.mapType);
    };

    this.create_gis_fallback_image = () => {
        let state = global.view.ol_driver.get_initial_frame();
        log.debug("MapGis.create_gis_fallback_image get initial frame", state);
        create_gis_fallback_image(state);
    };

    /**
     * set fallback media when selected context has changed
     */
    this.set_fallback_media = (id) => {
        fallback_media_id = id;
    };

    this.set_brightness = (brightness) => {
        gis_node.brightness = brightness;
        if (global.server.network.state === "offline" || this.is_gis_authorized === false) {
            map.image_node.brightness = brightness;
        }
    };

    this.unset_brightness = () => {
        gis_node.brightness = 1;
        if (global.server.network.state === "offline" || this.is_gis_authorized === false) {
            map.image_node.brightness = 1;
        }
    };

    /**
     * Activates the GIS map if necessary
     * @param {object} dataGis, center, resolution and image size
     */
    const update_gis = function (dataGis) {
        let gis = JSON.parse(JSON.stringify(dataGis));
        log.debug("MapGis.update_gis", gis);
        let ret = global.view.ol_driver.set_initial_frame(gis);
        if (ret) {
            log.debug("MapGis.update gis ret ok");
            map.set_map_type("gis");
            if (!global.view.apply_context()) {
                log.debug("MapGis.update_gis: init shared room");
                // No context found !  the GIS comes from collaboration,
                // init the center and the resolution so that all the initial viewstate 
                // holds in the full screen
                global.view.ol_driver.init_viewstate_full_screen(gis);
                //save gis info in context
                global.view.save_gis_context(gis);
            }
            $("#map_area button.add-media").hide();
            $("#map_area button.add-gis").hide();
        }
        return ret;
    };

    /**
     * Activates the GIS map after network recovery
     * @param {object} dataGis, center, resolution
     */
    const update_gis_from_initial_viewState = function (dataGis) {
        log.debug("MapGis.update_gis_from_initial_viewState", dataGis);

        let ret = global.view.ol_driver.set_initial_frame(dataGis);
        if (ret) {
            map.set_map_type("gis");

            if (!global.view.apply_context()) {
                log.debug("MapGis.update_gis_from_initial_viewState: init shared room");
                // No context found !  the GIS comes from collaboration,
                // init the center and the resolution so that all the initial viewstate 
                // holds in the full screen
                global.view.ol_driver.init_viewstate_full_screen(dataGis);
            }

            $("#map_area button.add-media").hide();
            $("#map_area button.add-gis").hide();
            global.event_bus.publish("scene.map.state", {loaded: true});
        }

        return ret;
    };

    /**
     * Activates the GIS map. Show tools for Area selection
     * @returns {boolean} true if success, false if google map error
     */
    const show_gis_init_assistant_tools = function () {
        log.debug("MapGis.show_gis_init_assistant_tools");

        // Hide/Disable menus and buttons
        $("#map_area button.add-media").hide();
        $("#map_area button.add-gis").hide();
        $("#command_area_left").hide();
        $("#command_map_right .btn-switcher-right").hide();
        $("#desktop_menu").hide();
        $("#desktop_tools").hide();
        global.tools.tools["recenter"].$button.hide();

        // Show/Enable assistant tools
        global.tools.tools["zoom_plus"].enable();
        global.tools.tools["zoom_minus"].enable();
        global.tools.tools["geocoding"].enable();
        global.tools.tools["geocoding"].$button.css("display", null);
        global.tools.tools["map_type_assistant"].$button.css("display", null);
        $("#command_map_right").css("display", null);
    };

    /**
     * Cancel the GIS map following click on Cancel by operator
     */
    const cancel_gis = function () {
        log.debug("MapGis.cancel_gis");

        // Reset map
        gis_node.close();
        map.set_map_type("media");

        // Hide/Disable assistant tools
        $("#command_map_right").hide();
        global.tools.tools["zoom_plus"].disable();
        global.tools.tools["zoom_minus"].disable();
        global.tools.tools["geocoding"].$button.hide();
        global.tools.tools["map_type_assistant"].$button.hide();

        // Show/Enable menus and buttons
        $("#map_area button.add-media").css("display", null);
        $("#map_area button.add-gis").css("display", null);
        $("#command_area_left").show();
        $("#command_map_right .btn-switcher-right").show();
        $("#desktop_menu").css("display", null);
        $("#desktop_tools").css("display", null);
        global.tools.tools["recenter"].$button.show();
    };

    /**
     * Close the GIS map
     * Notify that the image is no more loaded
     */
    const unset_gis = () => {
        log.debug("MapGis.unset_gis");
        map.set_map_type("media");

        global.tools.tools["geocoding"].$button.hide();
        global.tools.tools["map_type_assistant"].$button.hide();

        gis_node.close();
        global.event_bus.publish("scene.map.state", {loaded: false});
    };

    /**
     * Event to handle network offline (for gis map)
     * Then apply context, remove google map layers and load static image media
     */
    let handler_connection_lost = async () => {
        log.debug("MapGis.handler_connection_lost network offline");
        if (map.map_type !== "gis" || this.is_gis_authorized === false) {
            return;
        }
        global.view.apply_context();
        global.tools.tools["geocoding"].disable();
        global.tools.tools["map_type"].disable();
        // close google map layer
        map.set_map_object_brightness(gis_node.brightness);
        gis_node.close();

        // load media
        log.debug("MapGis.handler_connection_lost media cache ", fallback_media_id);
        var media = await global.medias.load_media({local_id: fallback_media_id});
        if (media) {
            // to avoid error if static media not yet created (can take some times with slow network)
            map.image_node.set_media(media);
        }

        // With static map -> disable dynamic resource scale
        // and show pictures
        global.resources.scale_reset();
    };

    /**
     * Event to handle network online (for gis map)
     * Unload static image media
     * Then activate GIS map and apply new center and resolution
     */
    let handler_connection_recovered = async () => {
        log.debug("-> on network online handler_connection_recovered");
        if (map.map_type !== "gis" || this.is_gis_authorized === false) {
            return;
        }
        global.tools.tools["geocoding"].enable();
        global.tools.tools["map_type"].enable();
        global.medias.unload_media({local_id: fallback_media_id});
        map.image_node.unset_media();

        let gis = {
            viewState: global.view.ol_driver.initial_viewState,
            size: global.view.ol_driver.initial_size,
            mapType: global.view.ol_driver.ol_customer_driver.mapBasicLayerType,
            tiles: global.view.ol_driver.initial_tiles_array,
            extent: global.view.ol_driver.initial_extent
        };
        update_gis_from_initial_viewState(gis);

        // center map to tracked unit if any
        if (global.scene.tracking.tracked !== undefined) {
            global.view.center_to_locked_position();
        }
    };

    global.event_bus.subscribe("network.offline", handler_connection_lost);
    global.event_bus.subscribe("network.online", handler_connection_recovered);
};

export default MapGis;
